// src/hooks/useRealtimeVoice.ts
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useRealtimeVoice
 *
 * Realtime Voice I/O layer for efa-chat.
 *
 * DESIGN RULES (PRESERVED):
 * - Audio I/O only (STT + future TTS)
 * - No conversation logic
 * - OCD v4 remains source of truth
 */

export interface RealtimeVoiceOptions {
  enabled?: boolean;
  onPartialTranscript?: (text: string) => void;
  onFinalTranscript?: (text: string) => void;
}

export interface RealtimeVoiceHandle {
  supported: boolean;
  isActive: boolean;
  isConnecting: boolean;

  // ✅ NEW (optional for callers)
  isSuspended: boolean;
  suspend: (v: boolean) => void;

  start: () => Promise<void>;
  stop: () => Promise<void>;
  speak: (text: string) => void;
}

type RealtimeEvent = {
  type?: string;
  [k: string]: any;
};

function extractEphemeralKey(data: any): string | null {
  if (!data) return null;

  // Newer common shape: { value: "ek_..." }
  if (typeof data.value === "string" && data.value.trim()) return data.value.trim();

  // Older/relay shape A: { client_secret: "ek_..." }
  if (typeof data.client_secret === "string" && data.client_secret.trim())
    return data.client_secret.trim();

  // Older/relay shape B: { client_secret: { value: "ek_..." } }
  if (typeof data.client_secret?.value === "string" && data.client_secret.value.trim())
    return data.client_secret.value.trim();

  return null;
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text();
  } catch {
    return "";
  }
}

export function useRealtimeVoice(
  options: RealtimeVoiceOptions = {}
): RealtimeVoiceHandle {
  const { enabled = false } = options;

  // ------------------------------------------------------------
  // ✅ STABLE CALLBACK REFS (CRITICAL)
  // ------------------------------------------------------------
  const partialCbRef =
    useRef<RealtimeVoiceOptions["onPartialTranscript"] | undefined>(undefined);

  const finalCbRef =
    useRef<RealtimeVoiceOptions["onFinalTranscript"] | undefined>(undefined);

  useEffect(() => {
    partialCbRef.current = options.onPartialTranscript;
    finalCbRef.current = options.onFinalTranscript;
  }, [options.onPartialTranscript, options.onFinalTranscript]);

  // ------------------------------------------------------------
  // Capability detection
  // ------------------------------------------------------------
  const supported =
    typeof window !== "undefined" &&
    typeof RTCPeerConnection !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  // ------------------------------------------------------------
  // State
  // ------------------------------------------------------------
  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // ✅ Suspend = keep session alive, but stop sending mic audio + ignore transcripts
  const [isSuspended, setIsSuspended] = useState(false);
  const suspendedRef = useRef(false);

  // ------------------------------------------------------------
  // Internal refs
  // ------------------------------------------------------------
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);

  // Buffer for partial transcription (NEW-format streaming)
  const partialBufferRef = useRef<string>("");

  // ✅ Suspend control: mute/unmute audio track without closing the session
  const suspend = useCallback((v: boolean) => {
    suspendedRef.current = v;
    setIsSuspended(v);

    // Mute/unmute outgoing mic audio
    const tracks = streamRef.current?.getAudioTracks?.() ?? [];
    for (const t of tracks) t.enabled = !v;

    // Also clear partial buffer when suspending to avoid stale deltas
    partialBufferRef.current = "";

//    console.log("[RealtimeVoice] suspend:", v);
  }, []);

  // ------------------------------------------------------------
  // Stop realtime voice
  // ------------------------------------------------------------
  const stop = useCallback(async () => {
    const hasAny =
      !!peerRef.current || !!streamRef.current || !!dataChannelRef.current;

    if (!isActive && !isConnecting && !hasAny) return;

    try {
      try {
        dataChannelRef.current?.close();
      } catch { }

      try {
        peerRef.current?.close();
      } catch { }

      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
      } catch { }
    } finally {
      peerRef.current = null;
      streamRef.current = null;
      dataChannelRef.current = null;
      partialBufferRef.current = "";

      setIsActive(false);
      setIsConnecting(false);

      suspendedRef.current = false;
      setIsSuspended(false);

//      console.log("[RealtimeVoice] stopped");
    }
  }, [isActive, isConnecting]);

  // ------------------------------------------------------------
  // Start realtime voice (WebRTC STT)
  // ------------------------------------------------------------
  const start = useCallback(async () => {
    if (!enabled) {
      console.warn("[RealtimeVoice] start() called but feature flag disabled");
      return;
    }
    if (!supported) {
      console.warn("[RealtimeVoice] environment does not support WebRTC audio");
      return;
    }
    if (isActive || isConnecting) return;

//    console.log("[RealtimeVoice] starting WebRTC session");
    setIsConnecting(true);

    //    const MODEL = "gpt-4o-realtime-preview";
    const MODEL = "gpt-realtime-1.5"; // gpt-realtime-1.5 or simply "gpt-realtime" for the latest stable version
    const CALLS_URL = "https://api.openai.com/v1/realtime/calls";
    const LEGACY_URL = `https://api.openai.com/v1/realtime?model=${encodeURIComponent(
      MODEL
    )}`;

    try {
      // 1️⃣ Get ephemeral token
      const tokenResp = await fetch(
        "https://efa-relay.dchang68.workers.dev/realtime/token",
        { method: "POST" }
      );

      if (!tokenResp.ok) {
        const errText = await safeReadText(tokenResp);
        throw new Error(
          `[RealtimeVoice] token failed: ${tokenResp.status} ${tokenResp.statusText} :: ${errText}`
        );
      }

      const tokenJson = await tokenResp.json().catch(() => ({}));
      const ephemeralKey = extractEphemeralKey(tokenJson);

      if (!ephemeralKey) {
        throw new Error(
          `[RealtimeVoice] missing ephemeral key in token response: ${JSON.stringify(
            tokenJson
          )}`
        );
      }

      // Safe debug (don’t log full token)
//      console.log("[RealtimeVoice] got ephemeral key:", {
//        prefix: ephemeralKey.slice(0, 3),
//        len: ephemeralKey.length,
//      });

      // 2️⃣ Create peer connection
      const pc = new RTCPeerConnection();
      peerRef.current = pc;

      // Fail-safe cleanup
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === "failed" || st === "disconnected" || st === "closed") {
//          console.log("[RealtimeVoice] connectionState:", st, "→ stopping");
          stop().catch(() => { });
        }
      };

      // 3️⃣ Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;

      // ✅ If already suspended (e.g., timer visible), immediately mute tracks
      if (suspendedRef.current) {
        stream.getAudioTracks().forEach((t) => (t.enabled = false));
      }

      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      // 4️⃣ Data channel for OpenAI realtime events
      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;

      dc.onopen = () => {
//        console.log("[RealtimeVoice] data channel open");

        /**
         * ✅ CRITICAL FIX:
         * Use the NEW session schema so the server actually enables VAD + transcription.
         *
         * Without this, you can connect successfully but receive NO:
         * - input_audio_buffer.speech_started
         * - transcription deltas/completed
         *
         * Matches current Realtime WebRTC docs. :contentReference[oaicite:1]{index=1}
         */
        try {
          dc.send(
            JSON.stringify({
              type: "session.update",
              session: {
                // STT-only: we want transcription events, but no model responses
                audio: {
                  input: {
                    turn_detection: {
                      type: "server_vad",
                      threshold: 0.35,
                      prefix_padding_ms: 1000,
                      silence_duration_ms: 1600,
                      create_response: false,
                    },
                    transcription: {
                      model: "gpt-4o-mini-transcribe",
                      language: "en",
                      //                      prompt: "Transcribe in English. For numbers, output Arabic digits (0-10), not words or other scripts.",
                      prompt:
                        "Transcribe spoken audio in English only. Output using Latin script only. Never output Chinese, Japanese, or Korean characters. For numbers from zero to ten, output Arabic digits 0-10. If audio is unclear, prefer the closest English transcription in ASCII rather than another language or script."
                    },
                  },
                },
                modalities: ["text"],
                instructions:
                  "Transcribe the user's speech only. Do not respond.",
              },
            })
          );
//          console.log("[RealtimeVoice] session.update sent (new schema)");
        } catch (e) {
//          console.warn("[RealtimeVoice] session.update send failed (ignored)", e);
        }
      };

      dc.onclose = () => console.log("[RealtimeVoice] data channel closed");
      dc.onerror = (e) => console.warn("[RealtimeVoice] data channel error", e);

      dc.onmessage = (event) => {
        let msg: RealtimeEvent | null = null;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        if (!msg?.type) return;

        // TEMP: keep this on while debugging so we can see speech_started etc.
//        console.log("[RealtimeVoice][event]", msg.type, msg);

        // ✅ If suspended, ignore transcript events (session stays alive)
        if (suspendedRef.current) {
          // still allow seeing VAD/buffer events if you want, but ignore transcripts
          if (
            msg.type === "conversation.item.input_audio_transcription.delta" ||
            msg.type === "conversation.item.input_audio_transcription.completed" ||
            msg.type === "response.audio_transcript.delta" ||
            msg.type === "response.audio_transcript.done"
          ) {
            return;
          }
        }

        // ----------------------------
        // VAD / buffer events (helpful for debugging)
        // ----------------------------
        if (msg.type === "input_audio_buffer.speech_started") return;
        if (msg.type === "input_audio_buffer.speech_stopped") return;
        if (msg.type === "input_audio_buffer.committed") return;

        // ----------------------------
        // NEW transcript event format
        // ----------------------------
        if (msg.type === "conversation.item.input_audio_transcription.delta") {
          const delta = typeof msg.delta === "string" ? msg.delta : "";
          if (!delta) return;

          partialBufferRef.current += delta;
          partialCbRef.current?.(partialBufferRef.current);
          return;
        }

        if (msg.type === "conversation.item.input_audio_transcription.completed") {
          // ✅ STRICT: only trust the explicit transcript field for this event.
          // Do NOT fall back to msg.text (it can contain non-transcript content / prompt echoes).
          const transcript = typeof msg.transcript === "string" ? msg.transcript : "";

          const finalText = transcript.trim();
          partialBufferRef.current = "";

          const looksLikePromptLeak =
            finalText.startsWith("context:") ||
            finalText.includes("Transcribe the user's speech") ||
            finalText.includes("Use ASCII characters") ||
            finalText.includes("Arabic digits");

          if (looksLikePromptLeak) {
 //           console.warn("[RealtimeVoice] blocked prompt-like final transcript", {
 //             preview: finalText.slice(0, 140),
 //           });
            return;
          }

          // Optional debug to confirm what you’re emitting
          if (finalText) {
 //           console.log("[RealtimeVoice] FINAL EMIT (new-format)", {
 //             preview: finalText.slice(0, 120),
 //             len: finalText.length,
 //           });
            finalCbRef.current?.(finalText);
          }
          return;
        }

        // ----------------------------
        // OLD transcript event format (older Realtime)
        // ----------------------------
        if (msg.type === "response.audio_transcript.delta") {
          const delta = typeof msg.delta === "string" ? msg.delta : "";
          if (delta.trim()) {
            partialCbRef.current?.(delta);
          }
          return;
        }

        if (msg.type === "response.audio_transcript.done") {
          const text = typeof msg.text === "string" ? msg.text.trim() : "";
          if (text) {
            finalCbRef.current?.(text);
          }
          return;
        }
      };

      // 5️⃣ SDP handshake
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const offerSdp = pc.localDescription?.sdp ?? offer.sdp ?? "";
      if (!offerSdp || offerSdp.length < 50) {
        throw new Error(
          `[RealtimeVoice] missing/short SDP offer. len=${offerSdp?.length ?? 0}`
        );
      }

//      console.log("[RealtimeVoice] sdp length:", offerSdp.length);

      async function postSdp(
        url: string,
        extraHeaders?: Record<string, string>
      ) {
        const resp = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
            ...(extraHeaders ?? {}),
          },
          body: offerSdp,
        });

        const text = await safeReadText(resp);
        if (!resp.ok) {
          throw new Error(
            `[RealtimeVoice] SDP failed ${resp.status} ${resp.statusText} :: ${text}`
          );
        }
        return text;
      }

      let answerSdp = "";

      // Try modern calls endpoint first; fall back to legacy URL
      try {
        console.log("[RealtimeVoice] SDP →", CALLS_URL);
        answerSdp = await postSdp(CALLS_URL);
      } catch (e1) {
        console.warn("[RealtimeVoice] calls SDP failed, trying legacy:", e1);
//        console.log("[RealtimeVoice] SDP →", LEGACY_URL);
        answerSdp = await postSdp(LEGACY_URL, { "OpenAI-Beta": "realtime=v1" });
      }

      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      setIsActive(true);
//      console.log("[RealtimeVoice] session active");
    } catch (err) {
      console.error("[RealtimeVoice] failed to start", err);
      await stop();
    } finally {
      setIsConnecting(false);
    }
  }, [enabled, supported, isActive, isConnecting, stop]);

  // ------------------------------------------------------------
  // Speak text (stub — future Step 10)
  // ------------------------------------------------------------
  const speak = useCallback(
    (text: string) => {
      if (!enabled || !isActive) return;
      if (!text || !text.trim()) return;
//      console.log("[RealtimeVoice] speak stub:", text);
    },
    [enabled, isActive]
  );

  // ------------------------------------------------------------
  // Cleanup
  // ------------------------------------------------------------
  useEffect(() => {
    return () => {
      // Best-effort cleanup
      try {
        dataChannelRef.current?.close();
      } catch { }
      try {
        peerRef.current?.close();
      } catch { }
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
      } catch { }
    };
  }, []);

  return {
    supported,
    isActive,
    isConnecting,
    isSuspended,
    suspend,
    start,
    stop,
    speak,
  };
}
